import moment from "moment";
import type { ContractAccessLog, ContractAccessLogPageTokenParams, ContractAccessLogPageTokenResponse } from "src/api/member/member-types";
import i18n from "src/i18n";
import type { ExcelColumn } from "src/pages/mypage/contract/hooks/useExcelDownload";
import type { AccessLogDateRange, AccessLogExcelRow, AccessLogListItem } from "../types/accessLog";
import type { TokenPage } from "./accessLogPageToken";

export const ACCESS_LOG_PAGE_SIZE = 20;
// TAC22 pageSize 최대값
export const ACCESS_LOG_DOWNLOAD_SIZE = 400;
export const INITIAL_ACCESS_LOG_DATE_RANGE: AccessLogDateRange = [moment().subtract(8, "days").toDate(), moment().toDate()];

export const ACCESS_LOG_EXCEL_COLUMNS: ExcelColumn<AccessLogExcelRow>[] = [
  { header: "일시", value: (row) => row.accessDate },
  { header: "이용자", value: (row) => row.phoneNumber },
  { header: "출입문 이름", value: (row) => row.deviceName },
  { header: "이벤트", value: (row) => row.eventName },
];

export const isSelectAccessLogDateRange = ([startDate, endDate]: AccessLogDateRange) => Boolean(startDate && endDate);

export const createAccessLogSearchParams = (
  contractManageId: number | string | undefined,
  targetDate: AccessLogDateRange,
  pageToken?: string,
  pageSize = ACCESS_LOG_PAGE_SIZE,
): ContractAccessLogPageTokenParams => {
  const [startDate, endDate] = targetDate;

  return {
    contractManageId: Number(contractManageId),
    startDate: moment(startDate).startOf("day").format(),
    endDate: moment(endDate).endOf("day").format(),
    pageSize,
    ...(pageToken ? { pageToken } : {}),
  };
};

export const createAccessLogExcelFileName = (contractApplyNumber: string | undefined, date: AccessLogDateRange) => {
  const [startDate, endDate] = date;

  return `Taap-출입로그-${contractApplyNumber}-${moment(startDate).startOf("day").format("YYYY-MM-DD")}_${moment(endDate)
    .endOf("day")
    .format("YYYY-MM-DD")}.xlsx`;
};

export const changePhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return undefined;

  const cp = phoneNumber.replace("+82", "0");
  const second = cp.substring(3, 11);
  const middle = second.substring(0, 4);
  const last = second.substring(4, 8);

  return `010-${middle}-${last}`;
};

// 마지막 페이지의 nextPageToken은 null로 옴
export const toAccessLogTokenPage = ({ logs, pagination }: ContractAccessLogPageTokenResponse): TokenPage<ContractAccessLog> => ({
  items: (logs ?? []).map((log) => ({ ...log, phoneNumber: changePhoneNumber(String(log.phoneNumber || "")) })),
  nextPageToken: pagination?.nextPageToken ?? undefined,
});

export const getAccessStatus = (eventGroup?: string): string => {
  switch (eventGroup) {
    case "ACCESS_SUCCESS":
      return i18n.t("accessAuthSuccess", { ns: "access" });
    case "ACCESS_FAILURE":
      return i18n.t("accessAuthFailure", { ns: "access" });
    case "ACCESS_REJECTED":
      return i18n.t("accessRejected", { ns: "access" });
    default:
      return "-";
  }
};

export const transformContractAccessLogData = (data: ContractAccessLog[]): AccessLogListItem[] =>
  data.map((item) => {
    return {
      id: item.id,
      accessDate: item.datetime ? moment(item.datetime).format("YYYY-MM-DD HH:mm:ss") : "-",
      phoneNumber: item.phoneNumber ?? "-",
      deviceName: item.deviceName ?? "-",
      eventName: item.eventName ? getAccessStatus(item.eventGroup) : "-",
    };
  });
